import {
  Button,
  ButtonMenu,
  Checkbox,
  collapseColumn,
  column,
  Css,
  emptyCell,
  type FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableApi,
  GridTableLayout,
  ModalBody,
  ModalFooter,
  ModalHeader,
  multiFilter,
  numericColumn,
  PageHeaderLayout,
  SelectField,
  simpleHeader,
  singleFilter,
  Tag,
  TagGroup,
  type TagGroupItem,
  TextField,
  Tooltip,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createProductOfferingUrl } from "src/RouteUrls";
import { priceCell } from "src/components";
import { Loading } from "src/components/Loading";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { Error } from "src/routes/Error";
import { createCheckoutStepSections } from "src/routes/components/ProductOfferingConfigUtils";
import {
  type ProductOfferingCostCalculatorPage_ComputeResultFragment,
  type ProductOfferingCostCalculatorPage_LineItemFragment,
  type ProductOfferingCostCalculatorPage_ProductOfferingFragment,
  useProductOfferingCostCalculatorMetaQuery,
  useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery,
  useProductOfferingCostCalculatorPage_ComputeLazyQuery,
} from "src/routes/developments/product-offerings/ProductOfferingCostCalculatorPage.generated";
import { type ProductOfferingCostCalculatorParams } from "src/routes/routesDef";
import { globalProductOfferingsRoute } from "src/routing/domains/librariesPaths";
import { fail, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { bcliCostLabel } from "./utils";

export function ProductOfferingCostCalculatorPage() {
  const { productOfferingId, rpavId } = useRouteParams<ProductOfferingCostCalculatorParams>();
  const query = useProductOfferingCostCalculatorMetaQuery({ variables: { id: productOfferingId, rpavId } });

  return queryResult(query, ({ productOffering }) => <DataView productOffering={productOffering} rpavId={rpavId} />);
}

type OptionGroup = ProductOfferingCostCalculatorPage_ProductOfferingFragment["optionGroups"][number];
type RpoOption = OptionGroup["options"][number];
type LineItemOption = ProductOfferingCostCalculatorPage_LineItemFragment["options"][number];
type OptionWithGroupOrder = { id: string; displayName: string; groupOrder: number };
/** A disabled option paired with an explicit reason (the missing prerequisites) for why it's disabled. */
type DisabledOption = { value: string; reason: string };
/** How an applied option is summarized in the config tags, i.e. its own tag vs a personalization count. */
type OptionTier = "design" | "structural" | "personalization";
type CurrentVersion = ProductOfferingCostCalculatorPage_ProductOfferingFragment["version"];
type AggregateVersion = ProductOfferingCostCalculatorPage_ProductOfferingFragment["aggregateVersions"][number];

type Totals = { totalCostInCents: number; previousTotalCostInCents?: number; differenceTotalCostInCents?: number };

type GroupRowData = {
  id: string;
  displayName: string;
  totalCostInCents: number;
  previousTotalCostInCents?: number;
  differenceTotalCostInCents?: number;
};

/** A tliv with context from its parent ProtoProjectItem, for grouping. */
type TlivEntry = Omit<ProductOfferingCostCalculatorPage_LineItemFragment, "totalCostInCents"> & {
  rowId: string;
  // Previous-only comparison rows intentionally leave this undefined so the Current column renders as blank.
  totalCostInCents?: number;
  costCodeId: string;
  costCodeName: string;
  tradePartnerId: string;
  tradePartnerName: string;
  locationId: string;
  /** The parent ProtoProjectItem's location, i.e. "1st Floor > Kitchen". */
  locationPath: string;
  /** Option tags from both versions when comparing costs. */
  taggedOptionIds: string[];
  /** The last option (by optionGroup.order), used for "Group By: Option". */
  lastOption?: OptionWithGroupOrder;
  previousTotalCostInCents?: number;
  differenceTotalCostInCents?: number;
};

type Row =
  | { kind: "header" }
  | { kind: "totals"; data: Totals }
  | { kind: "group"; data: GroupRowData }
  | { kind: "tliv"; data: TlivEntry };

type GroupBy = "costCode" | "tradePartner" | "option" | "none";
const groupByOptions: { id: GroupBy; name: string }[] = [
  { id: "costCode", name: "Cost Code" },
  { id: "tradePartner", name: "Trade Partner" },
  { id: "option", name: "Option" },
  { id: "none", name: "None" },
];

/** The controls shown in the cost table's persisted filter panel. */
type CostCalculatorFilter = { groupBy?: GroupBy; optionIds?: string[] };

type DataViewProps = { productOffering: ProductOfferingCostCalculatorPage_ProductOfferingFragment; rpavId: string };

/** Renders the page chrome (header + version selectors) and the option config / cost breakdown body. */
function DataView(props: DataViewProps) {
  const { productOffering, rpavId } = props;
  const [previousVersionId, setPreviousVersionId] = usePreviousVersionIdParam();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModal();
  const api = useGridTableApi<Row>();

  const currentVersion = productOffering.version;
  const previousVersion = useMemo(
    () => productOffering.aggregateVersions.find((version) => version.id === previousVersionId),
    [productOffering.aggregateVersions, previousVersionId],
  );

  // Parse initial option IDs from the `?options=1,2,3` query param (only on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialOptionIds = useMemo(() => parseOptionsParam(location.search), []);
  // The applied config drives the cost table; the draft selection lives in the Configure Options modal.
  const [appliedOptionIds, setAppliedOptionIds] = useState<string[]>(initialOptionIds);
  // The cost of the applied config. Held here rather than read off `query` because the modal computes its own
  // drafts, and Apply commits the result it already has instead of re-running the same expensive compute.
  const [appliedResult, setAppliedResult] = useState<ProductOfferingCostCalculatorPage_ComputeResultFragment>();

  // Sync applied options back to the `?options=` query param
  const syncOptionsToUrl = useCallback(
    (ids: string[]) => {
      const params = new URLSearchParams(location.search);
      if (ids.isEmpty) {
        params.delete("options");
      } else {
        // I.e. ["rpo:1", "rpo:2"] → "1-2"
        params.set("options", ids.map((id) => id.replace("rpo:", "")).join("-"));
      }
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    },
    [navigate, location.pathname, location.search],
  );

  useEffect(() => {
    syncOptionsToUrl(appliedOptionIds);
  }, [appliedOptionIds, syncOptionsToUrl]);

  const [runCurrentCompute, query] = useProductOfferingCostCalculatorPage_ComputeLazyQuery({ fetchPolicy: "no-cache" });
  const [runPreviousCompute, previousQuery] = useProductOfferingCostCalculatorPage_ComputeLazyQuery({
    fetchPolicy: "no-cache",
  });

  /**
   * Computes the cost of `optionIds` and stores the config the backend resolved from them.
   *
   * This covers the config changes made outside the modal, i.e. the `?options=` deep link, Load Checkout, and
   * a contingency change; the modal runs its own computes and commits them through `commitSelections`.
   */
  const applySelections = useCallback(
    async (optionIds: string[]) => {
      if (optionIds.isEmpty) {
        setAppliedOptionIds([]);
        setAppliedResult(undefined);
        return;
      }
      const { data } = await runCurrentCompute({
        variables: { readyPlanId: productOffering.id, readyPlanOptionIds: optionIds, versionId: currentVersion.id },
      });
      // The backend resolves the full config (e.g. adds dependent options); reopening the modal reflects it.
      if (data) {
        setAppliedOptionIds(data.computeReadyPlanCost.readyPlanOptions.map((option) => option.id));
        setAppliedResult(data.computeReadyPlanCost);
      }
    },
    [currentVersion.id, productOffering.id, runCurrentCompute],
  );

  /** Commits the config the modal already resolved, so Apply doesn't re-run the compute it just ran. */
  const commitSelections = useCallback(
    (optionIds: string[], result: ProductOfferingCostCalculatorPage_ComputeResultFragment) => {
      setAppliedOptionIds(optionIds);
      setAppliedResult(result);
    },
    [],
  );

  const didAutoApplyInitialOptions = useRef(false);

  useEffect(() => {
    if (didAutoApplyInitialOptions.current || initialOptionIds.isEmpty) return;
    didAutoApplyInitialOptions.current = true;
    void applySelections(initialOptionIds);
  }, [applySelections, initialOptionIds]);

  useEffect(() => {
    if (!previousVersionId || appliedOptionIds.isEmpty) return;
    void runPreviousCompute({
      variables: {
        readyPlanId: productOffering.id,
        readyPlanOptionIds: appliedOptionIds,
        versionId: previousVersionId,
      },
    });
  }, [appliedOptionIds, previousVersionId, productOffering.id, runPreviousCompute]);

  // Refetch when contingency markup changes so cost updates without other interaction
  useEffect(() => {
    if (appliedOptionIds.nonEmpty) {
      void applySelections(appliedOptionIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOffering.contingencyMarkupPercentage]);

  const configurationErrors = appliedResult?.configurationErrors;
  const resetSelections = useCallback(() => {
    setAppliedOptionIds([]);
    setAppliedResult(undefined);
  }, []);
  // Tags summarizing the applied config, shown with a trailing "Edit" control.
  const configTags = useMemo(
    () => summarizeAppliedConfig(productOffering.optionGroups, appliedOptionIds),
    [productOffering.optionGroups, appliedOptionIds],
  );

  const openConfigureOptions = useCallback(
    () =>
      openModal({
        size: "xl",
        content: (
          <ConfigureOptionsModal
            productOffering={productOffering}
            initialOptionIds={appliedOptionIds}
            onApply={commitSelections}
            onReset={resetSelections}
          />
        ),
      }),
    [appliedOptionIds, commitSelections, openModal, productOffering, resetSelections],
  );

  const csvPrefixRows: string[][] = [
    ["Plan", productOffering.displayName],
    // TODO: add computeProgramData back once the query field is uncommented
    // ["Sellable Sqft", String(query.data?.computeProgramData?.sellableSqft ?? "N/A")],
    ...(previousVersion
      ? [
          ["Previous Version", getAggregateVersionLabel(previousVersion)],
          ["Current Version", currentVersion.displayVersion],
        ]
      : [["Version", currentVersion.displayVersion]]),
  ];
  const csvFileName =
    [
      productOffering.displayName.replaceAll(".", "_"),
      ...(previousVersion
        ? [getAggregateVersionLabel(previousVersion), currentVersion.displayVersion]
        : [currentVersion.displayVersion]),
    ].join("_") + ".csv";

  return (
    <PageHeaderLayout
      pageHeader={{
        title: `${productOffering.displayName} — Cost Calculator`,
        breadcrumbs: {
          breadcrumbs: [
            { label: "Product Offerings", href: globalProductOfferingsRoute.url() },
            {
              label: productOffering.displayName,
              href: createProductOfferingUrl(
                productOffering.id,
                rpavId,
                productOffering.development?.id,
                productOffering.project?.id,
              ),
            },
          ],
        },
        rightSlot: (
          <div css={Css.df.aic.gap2.$}>
            {productOffering.aggregateVersions.length > 1 && (
              <ReadyPlanVersionHeader
                readyPlan={productOffering}
                versionId={previousVersionId}
                label="Previous Version"
                onSelect={setPreviousVersionId}
                allowUnset={true}
              />
            )}
            <ReadyPlanVersionHeader
              readyPlan={productOffering}
              versionId={productOffering.version.id}
              aggregateCheckoutId={productOffering.aggregateCheckout?.id}
            />
            <ButtonMenu
              trigger={{ icon: "verticalDots" }}
              placement="right"
              items={[
                { label: "Download CSV", onClick: () => api.downloadToCsv(csvFileName) },
                {
                  label: "Load Checkout",
                  onClick: () =>
                    openModal({ content: <LoadCheckoutModal onLoad={(ids) => void applySelections(ids)} /> }),
                },
              ]}
            />
          </div>
        ),
      }}
    >
      <div css={Css.df.gap1.m3.$}>
        {configTags.nonEmpty ? (
          <TagGroup tags={configTags} />
        ) : (
          <span css={Css.sm.gray700.$}>No options configured</span>
        )}
        <Button variant="text" label="Edit" onClick={openConfigureOptions} />
        {configurationErrors && configurationErrors.nonEmpty && (
          <Tooltip
            title={
              <ul css={Css.pl2.$}>
                {configurationErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            }
          >
            <Tag text={`${configurationErrors.length} Configuration Errors`} type="warning" />
          </Tooltip>
        )}
      </div>
      {query.error && <Error error={query.error} />}
      {!appliedResult && query.loading && <Loading />}
      {appliedResult &&
        (previousVersionId ? (
          queryResult(previousQuery, ({ computeReadyPlanCost: previousComputeReadyPlanCost }) => (
            <CostView
              api={api}
              currentResult={appliedResult}
              previousResult={previousComputeReadyPlanCost}
              currentVersion={currentVersion}
              previousVersion={previousVersion}
              csvPrefixRows={csvPrefixRows}
            />
          ))
        ) : (
          <CostView
            api={api}
            currentResult={appliedResult}
            currentVersion={currentVersion}
            csvPrefixRows={csvPrefixRows}
          />
        ))}
    </PageHeaderLayout>
  );
}

type ConfigureOptionsModalProps = {
  productOffering: ProductOfferingCostCalculatorPage_ProductOfferingFragment;
  initialOptionIds: string[];
  onApply: (optionIds: string[], result: ProductOfferingCostCalculatorPage_ComputeResultFragment) => void;
  onReset: () => void;
};

/**
 * Modal that holds the draft option configuration, with one control per RPOG in `RPOG.order`.
 *
 * Every change re-runs `computeReadyPlanCost` and adopts the config it resolves, i.e. the backend adds the
 * dependent/default options and drops the ones the change invalidated. That resolved config then drives which
 * groups are shown and which options are disabled, so the downstream groups settle out step by step as the user
 * works down the modal. Only Apply commits the config to the cost table.
 */
function ConfigureOptionsModal(props: ConfigureOptionsModalProps) {
  const { productOffering, initialOptionIds, onApply, onReset } = props;
  const { closeModal } = useModal();
  const [runCompute, { loading }] = useProductOfferingCostCalculatorPage_ComputeLazyQuery({ fetchPolicy: "no-cache" });

  // Modal-local draft IDs: `submit` updates them after each edit, while Apply passes them through `onApply` to the
  // page's `commitSelections`. Hydrate from the applied config, or seed the design packages when it is empty.
  const [draftOptionIds, setDraftOptionIds] = useState<string[]>(() =>
    initialOptionIds.nonEmpty ? initialOptionIds : seedDesignPackageOptions(productOffering.optionGroups),
  );
  // The cost of the currently-resolved draft, handed to `onApply` so the page doesn't recompute it.
  const [draftResult, setDraftResult] = useState<ProductOfferingCostCalculatorPage_ComputeResultFragment>();
  // Only the newest request may set state; an older, slower response would otherwise undo a later change.
  const latestRequest = useRef(0);

  /**
   * Sends `nextOptionIds` to the backend and adopts the config it resolves.
   *
   * `previousOptionIds` is the config being replaced, which lets the backend spot a design package change and
   * reset the personalization defaults that belonged to the old package.
   */
  const submit = useCallback(
    async (nextOptionIds: string[], previousOptionIds: string[]) => {
      const request = ++latestRequest.current;
      // Show the change right away, then let the resolved config correct it once the backend answers.
      setDraftOptionIds(nextOptionIds);
      setDraftResult(undefined);
      if (nextOptionIds.isEmpty) return;
      const { data } = await runCompute({
        variables: {
          readyPlanId: productOffering.id,
          readyPlanOptionIds: nextOptionIds,
          versionId: productOffering.version.id,
          originalReadyPlanOptionIds: previousOptionIds.nonEmpty ? previousOptionIds : undefined,
        },
      });
      if (request !== latestRequest.current) return;
      if (data) {
        setDraftOptionIds(data.computeReadyPlanCost.readyPlanOptions.map((option) => option.id));
        setDraftResult(data.computeReadyPlanCost);
      }
    },
    [productOffering.id, productOffering.version.id, runCompute],
  );

  // Resolve the seeded/applied config once on open, so the downstream groups are right before the first edit.
  // Nothing is changing yet, so send no `originalReadyPlanOptionIds`.
  useEffect(() => {
    void submit(draftOptionIds, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedOptionIds = useMemo(() => new Set(draftOptionIds), [draftOptionIds]);
  const disabledOptions = useMemo(
    () => getDisabledOptions(productOffering.optionGroups, draftOptionIds),
    [productOffering.optionGroups, draftOptionIds],
  );
  // Every active group stays in the list, so it doesn't jump around as selections change; the ones the config
  // can't use are disabled with the reason that names what would make them available.
  const shownGroupSections = useMemo(
    () =>
      createCheckoutStepSections(
        productOffering.optionGroups
          .filter((group) => group.active)
          .map((group) => ({ ...group, disabledReason: getGroupDisabledReason(group, selectedOptionIds) })),
      ),
    [productOffering.optionGroups, selectedOptionIds],
  );

  /** Selects `optionId` within `group`, replacing that group's current choice (an RPOG holds at most one option). */
  function selectOption(group: OptionGroup, optionId: string | undefined) {
    const groupOptionIds = new Set(group.options.map((option) => option.id));
    const others = draftOptionIds.filter((id) => !groupOptionIds.has(id));
    void submit(optionId ? [...others, optionId] : others, draftOptionIds);
  }

  return (
    <>
      <ModalHeader>Configure Options</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap4.$}>
          {shownGroupSections.map((section) => (
            <section key={section.code} css={Css.df.fdc.gap2.aifs.$}>
              <h2 data-testid="checkoutStep" css={Css.mdSb.gray900.$}>
                {section.title}
              </h2>
              {section.optionGroups.map((group) => (
                <OptionGroupField
                  key={group.id}
                  group={group}
                  selectedOptionIds={selectedOptionIds}
                  disabledOptions={disabledOptions}
                  disabledReason={group.disabledReason}
                  onSelect={(optionId) => selectOption(group, optionId)}
                />
              ))}
            </section>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="tertiary"
          label="Reset"
          disabled={draftOptionIds.isEmpty}
          onClick={() => {
            // Nothing left to resolve, so skip `submit` and just drop the config.
            latestRequest.current += 1;
            setDraftOptionIds([]);
            setDraftResult(undefined);
            onReset();
          }}
        />
        <Button variant="secondary" label="Cancel" onClick={closeModal} />
        <Button
          label="Apply"
          // Until the in-flight compute lands there is no cost to commit, so Apply waits for it.
          disabled={draftOptionIds.isEmpty || loading || !draftResult}
          onClick={() => {
            if (draftResult) onApply(draftOptionIds, draftResult);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type OptionGroupFieldProps = {
  group: OptionGroup;
  selectedOptionIds: Set<string>;
  disabledOptions: DisabledOption[];
  /** Set when the whole group is unavailable, i.e. its `requiredWhen` doesn't match; shown as a tooltip. */
  disabledReason: string | undefined;
  onSelect: (optionId: string | undefined) => void;
};

/**
 * Renders one RPOG's control: a checkbox for a single-option group, i.e. "Add Garage Bay", and a dropdown for a
 * group that offers a choice, i.e. Elevation.
 */
function OptionGroupField(props: OptionGroupFieldProps) {
  const { group, selectedOptionIds, disabledOptions, disabledReason, onSelect } = props;
  const options = group.options.filter((option) => option.active).sortBy((option) => option.displayName);
  if (options.isEmpty) return null;

  if (group.isSingleOptionGroup) {
    return (
      <div css={Css.df.fdc.gap1.$}>
        {/* A lone option carries the group's meaning already, so only name the group when there are several. */}
        {options.length > 1 && <span css={Css.sm.gray700.$}>{group.name}</span>}
        {options.map((option) => (
          <div key={option.id} css={Css.df.aic.gap1.$}>
            <Checkbox
              label={option.displayName}
              fullWidth
              selected={selectedOptionIds.has(option.id)}
              // A group-wide reason wins, since no option in it can be picked.
              disabled={disabledReason ?? disabledOptions.find((disabled) => disabled.value === option.id)?.reason}
              onChange={(selected) => onSelect(selected ? option.id : undefined)}
            />
            {!option.showInCheckout && (
              <Tooltip title="This option is not directly selectable in checkout.">
                <span css={Css.sm.gray700.$}>(Hidden)</span>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <SelectField
      label={group.name}
      value={options.find((option) => selectedOptionIds.has(option.id))?.id}
      onSelect={onSelect}
      options={options}
      getOptionValue={(o) => o.id}
      getOptionLabel={(o) => o.displayName}
      nothingSelectedText="—"
      disabled={disabledReason}
      disabledOptions={disabledOptions}
      sizeToContent
    />
  );
}

type CostViewProps = {
  api: GridTableApi<Row>;
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment;
  previousResult?: ProductOfferingCostCalculatorPage_ComputeResultFragment;
  currentVersion: CurrentVersion;
  previousVersion?: AggregateVersion;
  csvPrefixRows: string[][];
};

/** Renders the cost breakdown table with persisted search, grouping, and option filters. */
function CostView(props: CostViewProps) {
  const { api, currentResult, previousResult, currentVersion, previousVersion, csvPrefixRows } = props;
  const columns = useMemo(() => createColumns(currentVersion, previousVersion), [currentVersion, previousVersion]);
  const optionById = useMemo(() => createOptionById(currentResult, previousResult), [currentResult, previousResult]);
  const filterDefs = useMemo<FilterDefs<CostCalculatorFilter>>(
    () => ({
      groupBy: singleFilter({
        label: "Group By",
        options: groupByOptions,
        getOptionValue: (option) => option.id,
        getOptionLabel: (option) => option.name,
        defaultValue: "costCode",
      }),
      optionIds: multiFilter({
        label: "Option",
        options: Array.from(optionById.values()).sortBy((option) => option.displayName),
        getOptionValue: (option) => option.id,
        getOptionLabel: (option) => option.displayName,
      }),
    }),
    [optionById],
  );
  const layoutState = useGridTableLayoutState<CostCalculatorFilter>({
    persistedFilter: { filterDefs, storageKey: "productOfferingCostCalculatorFilter" },
    search: "client",
  });
  const groupBy = layoutState.filter.groupBy ?? "costCode";
  const rows = useMemo(() => {
    const selectedOptionIds = layoutState.filter.optionIds ?? [];
    return previousResult
      ? createComparisonRows(currentResult, previousResult, groupBy, optionById, selectedOptionIds)
      : createRows(currentResult, groupBy, optionById, selectedOptionIds);
  }, [currentResult, groupBy, layoutState.filter.optionIds, optionById, previousResult]);
  return (
    <GridTableLayout
      layoutState={layoutState}
      tableProps={{ api, columns, rows, csvPrefixRows, sorting: { on: "client" }, as: "virtual" }}
    />
  );
}

/** Creates the grid columns. */
function createColumns(currentVersion: CurrentVersion, previousVersion?: AggregateVersion): GridColumn<Row>[] {
  const groupColumn = column<Row>({
    id: "group",
    name: "Group",
    header: "Group",
    group: ({ displayName }) => displayName,
    tliv: emptyCell,
    totals: emptyCell,
    showIn: "csv",
  });
  const nameColumn = column<Row>({
    id: "name",
    name: "Name",
    header: "Name",
    group: ({ id, displayName }) => ({
      content: () => (
        <Tooltip title={id} placement="left">
          {displayName}
        </Tooltip>
      ),
      value: displayName,
    }),
    tliv: (li) => ({
      content: () => (
        <Tooltip title={li.id} placement="left">
          {li.displayName}
        </Tooltip>
      ),
      value: li.displayName,
    }),
    totals: emptyCell,
    w: "450px",
  });
  const locationColumn = column<Row>({
    id: "location",
    name: "Location",
    header: "Location",
    group: emptyCell,
    tliv: (li) => li.locationPath,
    totals: emptyCell,
    w: "250px",
  });
  const optionsColumn = column<Row>({
    id: "options",
    name: "Options",
    header: "Options",
    group: emptyCell,
    tliv: (li) => ({
      content: () => (
        <div css={Css.df.fdc.$}>
          {li.options.map((o) => (
            <div key={o.id}>{o.code}</div>
          ))}
        </div>
      ),
      value: li.options.map((o) => o.code).join(", "),
    }),
    totals: emptyCell,
    w: "250px",
  });
  const qtyColumn = numericColumn<Row>({
    id: "qty",
    name: "Qty",
    header: "Qty",
    group: emptyCell,
    tliv: (li) => li.quantity ?? emptyCell,
    totals: emptyCell,
    w: "80px",
  });
  const unitColumn = column<Row>({
    id: "unit",
    name: "Unit",
    header: "Unit",
    group: emptyCell,
    tliv: (li) => li.unitOfMeasure?.abbreviation ?? emptyCell,
    totals: emptyCell,
    w: "70px",
  });
  const unitCostColumn = numericColumn<Row>({
    id: "unitCost",
    name: "Unit Cost",
    header: "Unit Cost",
    group: emptyCell,
    tliv: (li) => tlivCostCell(li, li.unitCostInCents),
    totals: emptyCell,
    w: "120px",
  });
  const costSourceColumn = column<Row>({
    id: "costSource",
    name: "Cost Source",
    header: "Cost Source",
    group: emptyCell,
    tliv: (li) => {
      const revision = li.preferredBidContractLineItem?.revision;
      if (!revision) return emptyCell;
      return {
        content: () => <Link to={revision.blueprintUrl.path}>{revision.displayName}</Link>,
        value: revision.displayName,
      };
    },
    totals: emptyCell,
    w: "400px",
  });
  const comparisonMode = !!previousVersion;
  const costColumns = comparisonMode
    ? [
        numericColumn<Row>({
          id: "previous",
          name: "Previous",
          header: `Previous ${getAggregateVersionLabel(previousVersion)}`,
          group: (data) => priceCell({ valueInCents: data.previousTotalCostInCents }),
          tliv: (li) => priceCell({ valueInCents: li.previousTotalCostInCents }),
          totals: (totals) => priceCell({ valueInCents: totals.previousTotalCostInCents }),
          w: "150px",
        }),
        numericColumn<Row>({
          id: "current",
          name: "Current",
          header: `Current ${currentVersion.displayVersion}`,
          group: (data) => priceCell({ valueInCents: data.totalCostInCents }),
          tliv: (li) => tlivCostCell(li, li.totalCostInCents),
          totals: (totals) => priceCell({ valueInCents: totals.totalCostInCents }),
          w: "150px",
        }),
        numericColumn<Row>({
          id: "diff",
          name: "Difference",
          header: "Difference",
          group: (data) => priceCell({ valueInCents: data.differenceTotalCostInCents }),
          tliv: (li) => tlivCostCell(li, li.differenceTotalCostInCents),
          totals: (totals) => priceCell({ valueInCents: totals.differenceTotalCostInCents }),
          w: "150px",
        }),
      ]
    : [
        numericColumn<Row>({
          id: "totalCost",
          name: "Total Cost",
          header: "Total Cost",
          group: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          tliv: (li) => tlivCostCell(li, li.totalCostInCents),
          totals: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
          w: "120px",
        }),
      ];

  return [
    collapseColumn<Row>({ totals: emptyCell }),
    groupColumn,
    nameColumn,
    locationColumn,
    optionsColumn,
    qtyColumn,
    unitColumn,
    unitCostColumn,
    costSourceColumn,
    ...costColumns,
  ];
}

function tlivCostCell(li: Pick<TlivEntry, "preferredBidContractLineItem">, valueInCents: number | undefined) {
  const label = bcliCostLabel(li.preferredBidContractLineItem);
  return label ? { content: label, value: 0 } : priceCell({ valueInCents });
}

/** Creates the grid rows for a single version, optionally grouped by cost code, trade partner, or option. */
function createRows(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  groupBy: GroupBy,
  optionById: Map<string, OptionWithGroupOrder>,
  selectedOptionIds: string[],
): GridDataRow<Row>[] {
  const entries = filterTlivEntriesByOptions(createTlivEntries(currentResult, optionById), selectedOptionIds);
  const totals: Totals = {
    totalCostInCents: selectedOptionIds.nonEmpty
      ? entries.map((entry) => entry.totalCostInCents).sum()
      : currentResult.totalCostInCents,
  };
  return buildRows(entries, totals, groupBy);
}

function createComparisonRows(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  previousResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  groupBy: GroupBy,
  optionById: Map<string, OptionWithGroupOrder>,
  selectedOptionIds: string[],
): GridDataRow<Row>[] {
  const previousEntries = createTlivEntries(previousResult, optionById);
  const currentEntries = createTlivEntries(currentResult, optionById);
  const entries = filterTlivEntriesByOptions(mergeTlivEntries(previousEntries, currentEntries), selectedOptionIds);
  const filteredCurrentTotal = entries.map((entry) => entry.totalCostInCents).sum();
  const filteredPreviousTotal = entries.map((entry) => entry.previousTotalCostInCents).sum();
  const totals: Totals = selectedOptionIds.nonEmpty
    ? {
        totalCostInCents: filteredCurrentTotal,
        previousTotalCostInCents: filteredPreviousTotal,
        differenceTotalCostInCents: filteredCurrentTotal - filteredPreviousTotal,
      }
    : {
        totalCostInCents: currentResult.totalCostInCents,
        previousTotalCostInCents: previousResult.totalCostInCents,
        differenceTotalCostInCents: currentResult.totalCostInCents - previousResult.totalCostInCents,
      };
  return buildRows(entries, totals, groupBy);
}

function buildRows(entries: TlivEntry[], totals: Totals, groupBy: GroupBy): GridDataRow<Row>[] {
  if (groupBy === "none") {
    return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...toTlivRows(entries)];
  }
  // Group entries by the selected dimension
  const grouped = entries.groupBy((entry) => entryGroupKey(entry, groupBy));
  const groupEntries = Object.entries(grouped);
  const groupRows: GridDataRow<Row>[] = groupEntries.map(
    ([groupId, items]) =>
      ({
        kind: "group" as const,
        id: groupId,
        fixedSort: groupBy === "option" && groupId === "base" ? "first" : undefined,
        data: {
          id: groupId,
          displayName: entryGroupName(items[0], groupBy),
          totalCostInCents: items.map((entry) => entry.totalCostInCents).sum(),
          previousTotalCostInCents: items.map((entry) => entry.previousTotalCostInCents).sum(),
          differenceTotalCostInCents: items.map((entry) => entry.differenceTotalCostInCents).sum(),
        },
        children: toTlivRows(items),
      }) satisfies GridDataRow<Row>,
  );
  return [{ kind: "totals", id: "totals", data: totals }, simpleHeader, ...groupRows];
}

/** Glue together curr/previous options to get options active in both. */
function createOptionById(
  currentResult: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  previousResult?: ProductOfferingCostCalculatorPage_ComputeResultFragment,
): Map<string, OptionWithGroupOrder> {
  return [currentResult, previousResult]
    .compact()
    .flatMap((result) =>
      result.readyPlan.optionGroups.flatMap((rpog) =>
        rpog.options.map((rpo) => {
          return { ...rpo, groupOrder: rpog.order };
        }),
      ),
    )
    .uniqueBy((rpo) => rpo.id)
    .keyByObject((rpo) => rpo.id);
}

function createTlivEntries(
  result: ProductOfferingCostCalculatorPage_ComputeResultFragment,
  optionById: Map<string, OptionWithGroupOrder>,
): TlivEntry[] {
  // Immediately flatMap proto project items → tliv entries with parent context
  const entries: TlivEntry[] = result.protoProjectItems.flatMap((pi, piIdx) => {
    const lineItems = [pi.itemTemplateItem, ...pi.modifyingTemplateItems].compact();
    const tp = pi.preferredPotentialBidContractLineItem?.bidContractLineItem?.revision.bidContract.tradePartner;
    /** Shared grouping dimensions from the parent ProtoProjectItem. */
    function dims(opts: readonly LineItemOption[]) {
      const lastOption = opts
        .map(
          (option) =>
            optionById.get(option.id) ??
              // Even if we union "previous version options" + "current version options" the TLIs themselves
              // can still be tagged with active=false options, and so lead to a missing option error
              { id: option.id, displayName: `Archived Option ${option.id}`, groupOrder: Number.MAX_SAFE_INTEGER },
        )
        .sortBy((option) => option.groupOrder).last;
      return {
        costCodeId: pi.item.costCode.id,
        costCodeName: pi.item.costCode.displayName,
        tradePartnerId: tp?.id ?? "unassigned",
        tradePartnerName: tp?.name ?? "Homebound Internal Estimate",
        locationId: pi.location.id,
        locationPath: pi.location.displayLocationPath,
        taggedOptionIds: opts.map((option) => option.id),
        lastOption,
      };
    }
    if (lineItems.nonEmpty) {
      return lineItems.map((li) => ({ ...li, rowId: li.id, ...dims(li.options) }) as TlivEntry);
    }
    // Synthetic entry for proto items with no backing line items (e.g. contingency)
    return [
      {
        id: "tli:0",
        rowId: `pi-${piIdx}-tli:0`,
        displayName: pi.name,
        options: [],
        preferredBidContractLineItem: undefined,
        quantity: pi.quantity,
        unitCostInCents: 0,
        unitOfMeasure: pi.unitOfMeasure,
        totalCostInCents: pi.totalCostInCents,
        ...dims([]),
      } as TlivEntry,
    ];
  });
  return entries;
}

/** Converts tliv entries into GridDataRows. */
function toTlivRows(entries: TlivEntry[]): GridDataRow<Row>[] {
  return entries.map((e) => ({ kind: "tliv" as const, id: e.rowId, data: e }));
}

function mergeTlivEntries(previousEntries: TlivEntry[], currentEntries: TlivEntry[]): TlivEntry[] {
  const previousByLocationId = previousEntries.groupBy((entry) => entry.locationId);
  const currentByLocationId = currentEntries.groupBy((entry) => entry.locationId);
  const orderedLocationIds = [...previousEntries, ...currentEntries].map((entry) => entry.locationId).unique();
  return orderedLocationIds.flatMap((locationId) => {
    const previousMatches = previousByLocationId[locationId] ?? [];
    const currentMatches = currentByLocationId[locationId] ?? [];
    const pairCount = Math.max(previousMatches.length, currentMatches.length);
    return Array.from({ length: pairCount }, (_, index) => {
      const prevEntry = previousMatches[index];
      const currEntry = currentMatches[index];
      const baseEntry = currEntry ?? prevEntry ?? fail("Expected either a current or previous cost entry");
      const previousTotalCostInCents = prevEntry?.totalCostInCents;
      const currentTotalCostInCents = currEntry?.totalCostInCents;
      return {
        ...baseEntry,
        rowId: `comparison:${locationId}:${index}`,
        totalCostInCents: currentTotalCostInCents,
        previousTotalCostInCents,
        differenceTotalCostInCents: (currentTotalCostInCents ?? 0) - (previousTotalCostInCents ?? 0),
        taggedOptionIds: [...(prevEntry?.taggedOptionIds ?? []), ...(currEntry?.taggedOptionIds ?? [])].unique(),
      };
    });
  });
}

/** Keeps lines tagged with any selected option. */
function filterTlivEntriesByOptions(entries: TlivEntry[], selectedOptionIds: string[]): TlivEntry[] {
  if (selectedOptionIds.isEmpty) return entries;
  const selectedIds = new Set(selectedOptionIds);
  return entries.filter((entry) => entry.taggedOptionIds.some((optionId) => selectedIds.has(optionId)));
}

/** Returns the group key for a tliv entry based on the active groupBy mode. */
function entryGroupKey(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeId;
  if (groupBy === "tradePartner") return entry.tradePartnerId;
  return entry.lastOption?.id ?? "base";
}

/** Returns the group display name for a tliv entry based on the active groupBy mode. */
function entryGroupName(entry: TlivEntry, groupBy: GroupBy): string {
  if (groupBy === "costCode") return entry.costCodeName;
  if (groupBy === "tradePartner") return entry.tradePartnerName;
  return entry.lastOption?.displayName ?? "Base House";
}

/** Return the label for putting in the csv. */
function getAggregateVersionLabel(
  version: ProductOfferingCostCalculatorPage_ProductOfferingFragment["aggregateVersions"][number],
) {
  return version.status.code === AggregateVersionStatus.Draft
    ? `Draft of v${version.version ?? "?"}`
    : `v${version.version ?? "?"}`;
}

function usePreviousVersionIdParam() {
  const [{ prev }, setQueryParams] = useQueryParams({ prev: StringParam });
  const setPreviousVersionId = useCallback(
    (versionId: string | undefined) => {
      setQueryParams({ prev: versionId?.replace(":", "_") }, "replaceIn");
    },
    [setQueryParams],
  );
  return [prev?.replace("_", ":"), setPreviousVersionId] as const;
}

type LoadCheckoutModalProps = { onLoad: (ids: string[]) => void };

/** Modal that applies the selected options from a checkout config by ID. */
function LoadCheckoutModal(props: LoadCheckoutModalProps) {
  const { onLoad } = props;
  const { closeModal } = useModal();
  const [configId, setConfigId] = useState<string | undefined>();
  const [fetchCheckout, { loading, error }] = useProductOfferingCostCalculatorPage_CheckoutConfigLazyQuery();

  async function handleLoad() {
    if (!configId) return;
    const { data } = await fetchCheckout({ variables: { id: configId } });
    if (data) {
      onLoad(data.checkoutConfig.options.map((o) => o.id));
      closeModal();
    }
  }

  return (
    <>
      <ModalHeader>Load Checkout Config</ModalHeader>
      <ModalBody>
        <TextField label="Checkout Config ID" value={configId} onChange={setConfigId} placeholder="ccfg:1234" />
        {error && <div css={Css.sm.red600.mt1.$}>Failed to load: {error.message}</div>}
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label="Cancel" onClick={closeModal} />
        <Button label="Load" disabled={!configId || loading} onClick={handleLoad} />
      </ModalFooter>
    </>
  );
}

function getDisabledOptions(optionGroups: OptionGroup[], selectedOptionIds: string[]): DisabledOption[] {
  const optionNameById = new Map(optionGroups.flatMap((group) => group.options).map((o) => [o.id, o.displayName]));
  const selectedOptionIdsSet = new Set(selectedOptionIds);
  return optionGroups.flatMap((group) =>
    group.options
      .filter((option) => option.active)
      .map((option) => {
        const missingPrereqGroups = getUnsatisfiedPrerequisites(option, selectedOptionIdsSet);
        if (missingPrereqGroups.isEmpty) return undefined;
        // Each group is satisfied by any one of its prereqs (OR); every group must be satisfied (AND).
        // Multi-option groups are wrapped in parens to disambiguate, e.g. "(Modern or Contemporary) and Flat".
        const reason =
          "Requires " +
          missingPrereqGroups
            .map((prereqIds) => {
              const names = prereqIds.map((id) => optionNameById.get(id) ?? id);
              return names.length > 1 ? `(${names.join(" or ")})` : names[0];
            })
            .join(" and ");
        return { value: option.id, reason };
      })
      .compact(),
  );
}

/**
 * Returns the unsatisfied prerequisite groups for an option, where each entry is the list of option
 * IDs (any one of which satisfies that group). An empty result means all prerequisites are satisfied.
 */
function getUnsatisfiedPrerequisites(option: RpoOption, selectedOptionIds: Set<string>): string[][] {
  if (option.optionPrerequisites.isEmpty) return [];
  return option.optionPrerequisites
    .groupBy((prereq) => prereq.optionGroup.id)
    .toValues()
    .filter((prereqsInGroup) => !prereqsInGroup.some((prereq) => selectedOptionIds.has(prereq.id)))
    .map((prereqsInGroup) => prereqsInGroup.map((prereq) => prereq.id));
}

function hasSatisfiedOptionPrerequisites(option: RpoOption, selectedOptionIds: string[]): boolean {
  return getUnsatisfiedPrerequisites(option, new Set(selectedOptionIds)).isEmpty;
}

/**
 * Summarizes the applied config as tags. Package + structural options each get their own tag; the many
 * personalization options collapse into one count tag. I.e. "E1 - Craftsman", "Add Garage Bay", "28 personalization options".
 */
function summarizeAppliedConfig(optionGroups: OptionGroup[], appliedOptionIds: string[]): TagGroupItem[] {
  const tierByOptionId = createTierByOptionId(optionGroups);
  const appliedIds = new Set(appliedOptionIds);
  const designPackageTags: TagGroupItem[] = [];
  const structuralTags: TagGroupItem[] = [];
  let personalizationCount = 0;

  // One pass in group order (Elevation, Exterior Scheme, ...), bucketing each applied option by its tier.
  for (const group of optionGroups.sortBy((g) => g.order)) {
    for (const option of group.options) {
      if (!appliedIds.has(option.id)) continue;
      const { tier } = tierByOptionId.get(option.id) ?? fail(`Expected a tier for option ${option.id}`);
      if (tier === "design") designPackageTags.push({ text: option.displayName });
      else if (tier === "structural") structuralTags.push({ text: option.displayName });
      else personalizationCount += 1;
    }
  }

  return [
    ...designPackageTags,
    ...structuralTags,
    ...(personalizationCount > 0
      ? [{ text: `${personalizationCount} personalization option${personalizationCount === 1 ? "" : "s"}` }]
      : []),
  ];
}

/**
 * Indexes every option's tier + owning group, for the applied-config tags.
 *
 * Covers inactive groups and options too — an applied (or `?options=`) id can name an option whose group was
 * since deactivated, and it still needs to classify.
 *
 * I.e. rpo:1 → { groupId: "rpog:1", tier: "design" }
 */
function createTierByOptionId(optionGroups: OptionGroup[]): Map<string, { groupId: string; tier: OptionTier }> {
  const tierByOptionId = new Map<string, { groupId: string; tier: OptionTier }>();
  for (const group of optionGroups) {
    const isDesign = isDesignGroup(group);
    for (const option of group.options) {
      const tier: OptionTier = isDesign ? "design" : isStructuralOption(option) ? "structural" : "personalization";
      tierByOptionId.set(option.id, { groupId: group.id, tier });
    }
  }
  return tierByOptionId;
}

/**
 * Explains why an RPOG can't be used with the current selections, or returns undefined when it can.
 *
 * Only personalization RPOGs that carry a `requiredWhen` expression are conditional; every other group is always
 * selectable. The expression is a disjunction, so the group is available when any one clause matches, i.e. every
 * option in that clause's `all` is selected and none of its `none` are.
 *
 * The reason names the selections that would make the group available, i.e. a "Cabinet Hardware" group with
 * `[{ all: [Upgraded Kitchen], none: [] }]` reads "Requires Upgraded Kitchen". Clauses join with "or" and are
 * parenthesized when there is more than one, i.e. "Requires (Upgraded Kitchen) or (Chef Kitchen and not Galley)".
 * An empty clause list can never match, so no selection would help.
 */
function getGroupDisabledReason(group: OptionGroup, selectedOptionIds: Set<string>): string | undefined {
  if (!group.personalizationType || !group.requiredWhen) return undefined;
  const clauses = group.requiredWhen;
  const isAvailable = clauses.some(
    (clause) =>
      clause.all.every((option) => selectedOptionIds.has(option.id)) &&
      clause.none.every((option) => !selectedOptionIds.has(option.id)),
  );
  if (isAvailable) return undefined;
  if (clauses.isEmpty) return "Not available with the current selections";
  const described = clauses.map((clause) =>
    [
      ...clause.all.map((option) => option.displayName),
      ...clause.none.map((option) => `not ${option.displayName}`),
    ].join(" and "),
  );
  return `Requires ${described.length > 1 ? described.map((d) => `(${d})`).join(" or ") : described[0]}`;
}

/**
 * Picks a starting option for each design package RPOG, i.e. Elevation and Exterior Scheme.
 *
 * Only the design packages are seeded, because `computeReadyPlanCost` fills in the personalization defaults they
 * imply. Groups are walked in `order` so an earlier group's pick can satisfy a later option's prerequisites.
 */
function seedDesignPackageOptions(optionGroups: OptionGroup[]): string[] {
  const selectedOptionIds: string[] = [];
  for (const group of optionGroups.filter((g) => g.active && isDesignGroup(g)).sortBy((g) => g.order)) {
    const options = group.options.filter((o) => o.active);
    const option = options.find((o) => hasSatisfiedOptionPrerequisites(o, selectedOptionIds)) ?? options.first;
    if (option) selectedOptionIds.push(option.id);
  }
  return selectedOptionIds;
}

/**
 * True for the design-package RPOGs, i.e. Elevation / Exterior Scheme.
 *
 * The schema's `forDesignPackages` is just a server-side alias for this same `interior || exterior` OR,
 * so don't re-test it alongside this — every group is either a design group or not.
 */
function isDesignGroup(group: OptionGroup): boolean {
  return group.forDesignExterior || group.forDesignInterior;
}

/** True for structural & add-on options, i.e. "Add Garage Bay". */
function isStructuralOption(option: RpoOption): boolean {
  // `isStructuralV2`/`isAddOnV2` are nullable in the schema, so coerce to a real boolean.
  return !!(option.type.isStructuralV2 || option.type.isAddOnV2);
}

/** Parses `?options=1,2,3` into `["rpo:1", "rpo:2", "rpo:3"]`. */
function parseOptionsParam(search: string): string[] {
  const raw = new URLSearchParams(search).get("options");
  if (!raw) return [];
  return raw.split("-").map((id) => `rpo:${id.trim()}`);
}
